import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import Label from "../../../components/Label/Label";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBatchListForReportState,
  fetchFilteredBatchListForReport,
} from "../../../redux/reports/actions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "./reports.scss";

const StepSelectBatch = ({ formData, onChange, setFormData, mode }) => {
  const dispatch = useDispatch();
  const lastPreviewPayload = useRef(null);

  const batchState = formData.batchState || {};
  const { data: batchData = [], loader: batchLoader } =
    useSelector((state) => state.reports.filteredBatchListForReport) || {};

  const [previewData, setPreviewData] = useState([]);
  const [fetchedOnce, setFetchedOnce] = useState(false); // Tracks if Preview was clicked at least once

  const updateState = (field, value) => {
    onChange({ [field]: value });
  };

  // Initialize preview data once batchData is loaded
  useEffect(() => {
    if (!batchLoader && Array.isArray(batchData)) {
      const previouslySelected = formData.batchState?.batchIdList || [];

      // if first time (no selections yet) → select all
      const initialized = batchData.map((batch) => ({
        ...batch,
        isSelected:
          previouslySelected.length === 0
            ? true
            : previouslySelected.includes(batch.batch_uop_key),
      }));

      setPreviewData(initialized);

      // only set in parent if this is the first time (no selections previously)
      if (previouslySelected.length === 0 && batchData.length > 0) {
        updateState(
          "batchIdList",
          initialized.map((b) => b.batch_uop_key)
        );
      }
    }
  }, [batchLoader, batchData]);

  const handleRadioChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      batchState: {
        ...prev.batchState,
        batchRangeOption: value,
        recentBatchCount: value === "by_latest_x" ? "20" : "",
        recentDayCount: "",
        startDate: null,
        endDate: null,
        batchIdList: [],
      },
    }));
    setPreviewData([]);
    dispatch(clearBatchListForReportState()); // clear from Redux if applicable
    setFetchedOnce(false);
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date)) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const onClickPreview = () => {
    const payload = {
      drug_product_id: formData?.product?.product_id,
      site_id: formData?.product?.site_id,
      batchs_selection: {
        batch_range_option: batchState?.batchRangeOption,
        recent_batch_count: batchState?.recentBatchCount,
        recent_day_count: batchState?.recentDayCount,
        start_date: formatDate(batchState?.startDate),
        end_date: formatDate(batchState?.endDate),
      },
    };

    // Clear data before fetching
    setPreviewData([]);
    updateState("batchIdList", []);
    setFetchedOnce(true); // ✅ Moved here (only set true when Preview is clicked)
    dispatch(fetchFilteredBatchListForReport(payload));
    lastPreviewPayload.current = payload;
  };

  const handleCheckboxChange = (batchId) => {
    setPreviewData((prev) => {
      const updated = prev.map((batch) =>
        batch.batch_uop_key === batchId
          ? { ...batch, isSelected: !batch.isSelected }
          : batch
      );
      updateState(
        "batchIdList",
        updated.filter((b) => b.isSelected).map((b) => b.batch_uop_key)
      );
      return updated;
    });
  };

  const handleSelectAll = (checked) => {
    const updated = previewData.map((b) => ({ ...b, isSelected: checked }));
    setPreviewData(updated);
    updateState("batchIdList", checked ? updated.map((b) => b.batch_uop_key) : []);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Card elevation={0} sx={{ flex: "0 0 auto" }}>
        <CardContent sx={{ p: 0 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Label text="Batch Range" fontWeight="700" fontSize="14px" />
            </Grid>
            <Grid item>
              <RadioGroup
                row
                value={batchState.batchRangeOption}
                onChange={handleRadioChange}
                className="batch-range-options"
              >
                <FormControlLabel
                  value="by_latest_x"
                  control={<Radio size="small" />}
                  label={
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Recent Batches"
                      value={batchState.recentBatchCount || ""}
                      onChange={(e) =>
                        updateState("recentBatchCount", e.target.value)
                      }
                      disabled={batchState.batchRangeOption !== "by_latest_x"}
                    />
                  }
                />
                <FormControlLabel
                  value="by_recent_x_days"
                  control={<Radio size="small" />}
                  label={
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Recent Days"
                      value={batchState.recentDayCount || ""}
                      onChange={(e) =>
                        updateState("recentDayCount", e.target.value)
                      }
                      disabled={
                        batchState.batchRangeOption !== "by_recent_x_days"
                      }
                    />
                  }
                />
                <FormControlLabel
                  value="by_date_x"
                  control={<Radio size="small" />}
                  className="batch-range-radio"
                  label={
                    <Box
                      sx={{ display: "flex", gap: 1 }}
                      className="date-range-picker-report"
                    >
                      {[
                        ["startDate", "From"],
                        ["endDate", "To"],
                      ].map(([key, placeholder]) => (
                        <DatePicker
                          key={key}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={50}
                          popperClassName="custom-datepicker-popper"
                          selected={
                            batchState[key] instanceof Date
                              ? batchState[key]
                              : null
                          }
                          onChange={(date) => updateState(key, date)}
                          selectsStart={key === "startDate"}
                          dateFormat="dd-MMM-yyyy"
                          selectsEnd={key === "endDate"}
                          startDate={
                            batchState.startDate instanceof Date
                              ? batchState.startDate
                              : null
                          }
                          endDate={
                            batchState.endDate instanceof Date
                              ? batchState.endDate
                              : null
                          }
                          minDate={
                            key === "endDate" &&
                            batchState.startDate instanceof Date
                              ? batchState.startDate
                              : undefined
                          }
                          maxDate={
                            key === "startDate"
                              ? batchState.endDate instanceof Date
                                ? batchState.endDate
                                : new Date()
                              : new Date()
                          }
                          placeholderText={placeholder}
                          disabled={batchState.batchRangeOption !== "by_date_x"}
                          fixedHeight
                          renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            changeYear,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                          }) => (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 1.5,
                                py: 1,
                                bgcolor: "primary.main",
                                color: "common.white",
                                borderRadius: "8px 8px 0 0",
                              }}
                            >
                              <IconButton
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                sx={{ color: "common.white", p: 0.5 }}
                                size="small"
                              >
                                <ArrowBackIosIcon fontSize="small" />
                              </IconButton>

                              <Box
                                sx={{
                                  mx: 1,
                                  fontWeight: 500,
                                  cursor: "default",
                                  width: 80,
                                  textAlign: "center",
                                }}
                              >
                                <Label
                                  text={date.toLocaleString("default", {
                                    month: "long",
                                  })}
                                  color="#ffff"
                                  fontSize={16}
                                  fontWeight={600}
                                />
                              </Box>

                              <Select
                                value={date.getFullYear()}
                                onChange={(e) =>
                                  changeYear(Number(e.target.value))
                                }
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  fontSize: 13,
                                  color: "text.primary",
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  // "& .MuiSelect-select": { py: 0.25, px: 1 },
                                }}
                              >
                                {Array.from(
                                  { length: 50 },
                                  (_, i) => new Date().getFullYear() - i
                                ).map((y) => (
                                  <MenuItem key={y} value={y}>
                                    {y}
                                  </MenuItem>
                                ))}
                              </Select>

                              <IconButton
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                sx={{ color: "common.white", p: 0.5 }}
                                size="small"
                              >
                                <ArrowForwardIosIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                          customInput={
                            <TextField
                              size="small"
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CalendarTodayIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          }
                        />
                      ))}
                    </Box>
                  }
                />
              </RadioGroup>
            </Grid>
            <Grid item xs="auto" sx={{ ml: "auto" }}>
              <Button
                variant="outlined"
                startIcon={<PreviewOutlinedIcon />}
                onClick={onClickPreview}
              >
                <Label text="Preview" color="inherit" />
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {batchLoader ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress />
          </Box>
        ) : previewData.length > 0 ? (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        indeterminate={
                          previewData.some((b) => b.isSelected) &&
                          !previewData.every((b) => b.isSelected)
                        }
                        checked={
                          previewData.length > 0 &&
                          previewData.every((b) => b.isSelected)
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{ p: 0 }}
                      />
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {`${previewData.filter((b) => b.isSelected).length} / ${previewData.length}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 2,
                    }}
                  >
                    <Label text="Batch ID" fontSize="14px" fontWeight="700" />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 2,
                    }}
                  >
                    <Label text="Batch Date" fontSize="14px" fontWeight="700" />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 2,
                    }}
                  >
                    <Label text="Unit Operation" fontSize="14px" fontWeight="700" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.map((batch) => (
                  <TableRow key={batch.batch_uop_key} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={batch.isSelected}
                        onChange={() => handleCheckboxChange(batch.batch_uop_key)}
                      />
                    </TableCell>
                    <TableCell>
                      <Label text={batch.batch_id_non_unique} />
                    </TableCell>
                    <TableCell>
                      <Label text={batch['Batch Date']} />
                    </TableCell>
                    <TableCell>
                      <Label text={batch['Unit Operation']} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : fetchedOnce ? (
          <Label
            textAlign="center"
            text="No batches available for the selected filter."
          />
        ) : (
          <Label
            textAlign="center"
            text="No batches fetched. Please select a filter and click Preview."
          />
        )}
      </Box>
    </Box>
  );
};

export default StepSelectBatch;
